import React from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import UserAvatar from "@/components/site/UserAvatar";
import BackButton from "@/components/site/BackButton";
import { LogOut } from "lucide-react";

const links = [
  { to: "/dashboard", label: "Dashboard", testid: "app-nav-dashboard" },
  { to: "/workspace", label: "Workspace", testid: "app-nav-workspace" },
  { to: "/director", label: "Director", testid: "app-nav-director" },
  { to: "/history", label: "History", testid: "app-nav-history" },
  { to: "/billing", label: "Billing", testid: "app-nav-billing" },
  { to: "/account", label: "My Account", testid: "app-nav-account" },
];

export default function AppNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  async function handleLogout() {
    await logout();
    navigate("/");
  }

  return (
    <header className="border-b border-border bg-background sticky top-0 z-30" data-testid="app-nav">
      <div className="max-w-[1400px] mx-auto flex items-stretch">
        <Link to="/" className="flex items-center gap-3 px-6 py-3 border-r border-border" data-testid="app-brand">
          <span className="w-2 h-2 bg-primary rotate-45 inline-block" />
          <span className="font-serif-display text-xl leading-none tracking-tight">Draftwave</span>
        </Link>
        <nav className="hidden md:flex items-stretch">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `overline px-4 py-4 border-r border-border hover-invert ${
                  isActive ? "text-primary" : "text-foreground hover:text-primary"
                }`
              }
              data-testid={l.testid}
            >
              {l.label}
            </NavLink>
          ))}
          {user?.is_admin && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                `overline px-4 py-4 border-r border-border ${isActive ? "text-primary" : "text-foreground hover:text-primary"}`
              }
              data-testid="app-nav-admin"
            >
              Admin
            </NavLink>
          )}
        </nav>
        <div className="ml-auto flex items-stretch">
          <Link
            to="/account"
            className="hidden md:flex items-center gap-3 px-4 border-l border-border hover:bg-muted/40 transition"
            data-testid="app-nav-avatar"
          >
            <UserAvatar user={user} size={34} testid="app-nav-user-avatar" />
            <div className="flex flex-col justify-center">
              <div className="text-sm font-medium leading-tight" data-testid="app-nav-user-name">
                {user?.name || user?.email}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                {user?.username ? `@${user.username}` : `${user?.plan} · ${user?.plan_status}`}
              </div>
            </div>
          </Link>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="rounded-none border-l border-border h-auto px-5"
            data-testid="app-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden md:inline overline">Sign out</span>
          </Button>
        </div>
      </div>
      {pathname !== "/dashboard" && (
        <div className="max-w-[1400px] mx-auto px-6 py-2 border-t border-border/60 flex items-center">
          <BackButton fallback="/dashboard" />
        </div>
      )}
    </header>
  );
}
